// src/lib/api.ts - Improved API with centralized types
import {
  // Base types
  StrapiResponse,
  StrapiSingleResponse,
  StrapiMedia,
  
  // Content types
  Homepage,
  NewsArticle,
  Organization,
  Publication,
  ResearchTheme,
  SiteSettings,
  ContactInformation,
  Person,
  Tag,
  
  // Enum types
  NewsCategory,
  OrganizationType,
  PartnershipLevel,
  PersonType,
  
  // Error types
  ApiError,
  NetworkError,
  ApiResult,
  
  // Utility types
  Nullable,
  PaginationParams,
  SearchFilters,
  
  // Type guards
  isOrganization,
  isNewsArticle,
  isResearchTheme,
  isStrapiMedia,
} from '@/types';

// Environment configuration
const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337';
const API_BASE = `${STRAPI_URL}/api`;

// =====================
// API CLIENT CLASS
// =====================

class ApiClient {
  private baseUrl: string;
  private abortController: AbortController | null = null;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  // Abort ongoing requests
  public abort(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  // Generic request method with improved error handling
  private async request<T>(
    endpoint: string, 
    options?: RequestInit
  ): Promise<T> {
    this.abortController = new AbortController();
    const url = `${this.baseUrl}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        signal: this.abortController.signal,
        ...options,
      });

      if (!response.ok) {
        throw new ApiError(
          `API request failed: ${response.status} ${response.statusText}`,
          response.status,
          endpoint
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new NetworkError('Request was cancelled', endpoint);
      }
      
      if (error instanceof ApiError) {
        throw error;
      }
      
      console.error(`API request error for ${url}:`, error);
      throw new NetworkError(
        error instanceof Error ? error.message : 'Unknown network error',
        endpoint
      );
    }
  }

  // Generic fetch with error handling and fallback
  private async fetchWithErrorHandling<T>(
    endpoint: string,
    fallback: T,
    validator?: (data: any) => data is T
  ): Promise<T> {
    try {
      const response = await this.request<StrapiSingleResponse<T> | StrapiResponse<T[]>>(endpoint);
      
      // Handle both single and array responses
      let data: any;
      if ('data' in response) {
        data = response.data;
      } else {
        data = response;
      }

      // Validate data if validator provided
      if (validator && !validator(data)) {
        console.warn(`Data validation failed for ${endpoint}`, data);
        return fallback;
      }

      return data as T;
    } catch (error) {
      console.error(`Failed to fetch ${endpoint}:`, error);
      return fallback;
    }
  }

  // =====================
  // HOMEPAGE API
  // =====================

  async getHomepage(): Promise<Nullable<Homepage>> {
    try {
      const response = await this.request<StrapiSingleResponse<Homepage>>(
        '/homepage?populate=*'
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch homepage:', error);
      return null;
    }
  }

  // =====================
  // NEWS ARTICLES API
  // =====================

  async getNewsArticles(params?: {
    page?: number;
    pageSize?: number;
    featured?: boolean;
    category?: NewsCategory;
    filters?: SearchFilters;
  }): Promise<NewsArticle[]> {
    try {
      console.log('Fetching news articles with params:', params);
      
      const searchParams = new URLSearchParams();
      
      // Pagination
      if (params?.page) {
        searchParams.set('pagination[page]', params.page.toString());
      }
      if (params?.pageSize) {
        searchParams.set('pagination[pageSize]', params.pageSize.toString());
      }
      
      // Filters
      if (params?.featured !== undefined) {
        searchParams.set('filters[isFeatured][$eq]', params.featured.toString());
      }
      if (params?.category) {
        searchParams.set('filters[category][$eq]', params.category);
      }
      
      // Search filters
      if (params?.filters?.query) {
        searchParams.set('filters[$or][0][title][$containsi]', params.filters.query);
        searchParams.set('filters[$or][1][excerpt][$containsi]', params.filters.query);
      }
      if (params?.filters?.dateFrom) {
        searchParams.set('filters[publishedDate][$gte]', params.filters.dateFrom);
      }
      if (params?.filters?.dateTo) {
        searchParams.set('filters[publishedDate][$lte]', params.filters.dateTo);
      }

      // Simplified populate and sort - this might be causing the 400 error
      searchParams.set('populate', '*');
      searchParams.set('sort', 'publishedDate:desc');

      const url = `/news-articles?${searchParams.toString()}`;
      console.log('Final news API URL:', url);

      const response = await this.request<StrapiResponse<NewsArticle[]>>(url);
      
      console.log('News articles response:', response);
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Invalid news response structure:', response);
        return [];
      }
      
      return response.data;
      
    } catch (error) {
      console.error('Failed to fetch news articles:', error);
      
      // Try a simpler fallback request
      try {
        console.log('Trying fallback news request...');
        const fallbackResponse = await this.request<StrapiResponse<NewsArticle[]>>(
          '/news-articles?populate=*'
        );
        
        return fallbackResponse.data || [];
      } catch (fallbackError) {
        console.error('Fallback news request also failed:', fallbackError);
        return [];
      }
    }
  }

  async getLatestNews(limit: number = 5): Promise<NewsArticle[]> {
    try {
      console.log(`Fetching latest ${limit} news articles...`);
      
      // Try the simplest possible request first
      const response = await this.request<StrapiResponse<NewsArticle[]>>(
        `/news-articles?populate=*&sort=publishedDate:desc&pagination[pageSize]=${limit}`
      );
      
      console.log('Latest news response:', response);
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Invalid latest news response structure:', response);
        return [];
      }
      
      return response.data;
      
    } catch (error) {
      console.error('Failed to fetch latest news:', error);
      
      // Try even simpler fallback
      try {
        console.log('Trying simplest news request...');
        const fallbackResponse = await this.request<StrapiResponse<NewsArticle[]>>(
          '/news-articles'
        );
        
        const articles = fallbackResponse.data || [];
        // Sort and limit in JavaScript if API sorting fails
        return articles
          .sort((a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime())
          .slice(0, limit);
          
      } catch (fallbackError) {
        console.error('All news requests failed:', fallbackError);
        return [];
      }
    }
  }

  async getFeaturedNews(limit: number = 3): Promise<NewsArticle[]> {
    return this.getNewsArticles({ featured: true, pageSize: limit });
  }

  async getNewsByCategory(category: NewsCategory, limit: number = 10): Promise<NewsArticle[]> {
    return this.getNewsArticles({ category, pageSize: limit });
  }

  // =====================
  // ORGANIZATIONS API
  // =====================

  async getPartnerOrganizations(): Promise<Organization[]> {
    try {
      console.log('Fetching organizations from API...');
      
      const response = await this.request<StrapiResponse<Organization[]>>(
        '/organizations?populate=*'
      );
      
      console.log('Raw API response:', response);
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Invalid response structure:', response);
        return [];
      }
      
      // Filter and sort organizations
      const organizations = response.data
        .filter((org: Organization) => {
          if (!isOrganization(org)) {
            console.warn('Invalid organization data:', org);
            return false;
          }
          
          // Handle both possible data structures
          const isActive = org.attributes?.isActive ?? org.isActive;
          return isActive === true;
        })
        .sort((a: Organization, b: Organization) => {
          // Handle both possible data structures for sorting
          const aName = a.attributes?.name ?? a.name ?? '';
          const bName = b.attributes?.name ?? b.name ?? '';
          const aLevel = a.attributes?.partnershipLevel ?? a.partnershipLevel ?? 'Affiliated';
          const bLevel = b.attributes?.partnershipLevel ?? b.partnershipLevel ?? 'Affiliated';
          
          const levelOrder: Record<PartnershipLevel, number> = { 
            'Primary': 1, 
            'Secondary': 2, 
            'Affiliated': 3 
          };
          
          const aLevelNum = levelOrder[aLevel] || 4;
          const bLevelNum = levelOrder[bLevel] || 4;
          
          if (aLevelNum !== bLevelNum) {
            return aLevelNum - bLevelNum;
          }
          return aName.localeCompare(bName);
        });
      
      console.log('Filtered and sorted organizations:', organizations);
      return organizations;
      
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      return [];
    }
  }

 async getFeaturedPartners(): Promise<{
  primary: Organization[];
  secondary: Organization[];
  tertiary: Organization[];
}> {
  try {
    console.log('Fetching all featured partners from API...');
    
    const searchParams = new URLSearchParams();

    // Get all active organizations
    searchParams.set('filters[isActive][$eq]', 'true');

    // Tiers shown on the home page:
    //   primary   = featuredPartner
    //   secondary = secondaryFeaturedPartner
    //   tertiary  = leadPartner  (rendered as the "Lead Industry Partners" row)
    searchParams.set('filters[$or][0][featuredPartner][$eq]', 'true');
    searchParams.set('filters[$or][1][secondaryFeaturedPartner][$eq]', 'true');
    searchParams.set('filters[$or][2][leadPartner][$eq]', 'true');
    
    // Populate all related data
    searchParams.set('populate', '*');
    
    // Sort by partnership level then name
    searchParams.set('sort[0]', 'partnershipLevel:asc');
    searchParams.set('sort[1]', 'name:asc');

    const url = `/organizations?${searchParams.toString()}`;
    console.log('Featured Partners API URL:', url);

    const response = await this.request<StrapiResponse<Organization[]>>(url);
    
    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid featured partners response structure:', response);
      return { primary: [], secondary: [], tertiary: [] };
    }
    
    // Categorize partners into tiers
    const primary: Organization[] = [];
    const secondary: Organization[] = [];
    const tertiary: Organization[] = [];
    
    response.data.forEach((org: Organization) => {
      if (!isOrganization(org)) {
        console.warn('Invalid organization data:', org);
        return;
      }
      
      // Handle both possible data structures
      const isPrimary = org.attributes?.featuredPartner ?? org.featuredPartner;
      const isSecondary = org.attributes?.secondaryFeaturedPartner ?? org.secondaryFeaturedPartner;
      const isLead = org.attributes?.leadPartner ?? org.leadPartner;
      const isActive = org.attributes?.isActive ?? org.isActive;

      if (!isActive) return;

      if (isPrimary) {
        primary.push(org);
      } else if (isSecondary) {
        secondary.push(org);
      } else if (isLead) {
        tertiary.push(org);
      }
    });
    
    console.log('Categorized featured partners:', { primary, secondary, tertiary });
    return { primary, secondary, tertiary };
    
  } catch (error) {
    console.error('Failed to fetch featured partners:', error);
    return { primary: [], secondary: [], tertiary: [] };
  }
}

  // =====================
  // RESEARCH THEMES API
  // =====================

  async getResearchThemes(): Promise<ResearchTheme[]> {
    try {
      console.log('Fetching research themes from API...');
      
      const response = await this.request<StrapiResponse<ResearchTheme[]>>(
        '/research-themes?populate=*'
      );
      
      console.log('Raw research themes response:', response);
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Invalid research themes response structure:', response);
        return [];
      }
      
      // Validate and sort themes
      const validThemes = response.data.filter(isResearchTheme);
      
      const sortedThemes = validThemes.sort((a, b) => {
        const aNum = parseInt(a.themeNumber.replace('T', '')) || 999;
        const bNum = parseInt(b.themeNumber.replace('T', '')) || 999;
        return aNum - bNum;
      });
      
      console.log('Sorted research themes:', sortedThemes);
      return sortedThemes;
      
    } catch (error) {
      console.error('Failed to fetch research themes:', error);
      
      // Try fallback request with explicit per-field populate (Strapi v5 array
      // syntax — the v4-style `populate[field]=*` is invalid in v5 and 400s).
      try {
        console.log('Trying fallback research themes request...');
        const fallbackResponse = await this.request<StrapiResponse<ResearchTheme[]>>(
          '/research-themes?populate[0]=featuredImage&populate[1]=leadResearchers&populate[2]=relatedPartners'
        );
        
        const themes = fallbackResponse.data || [];
        return themes
          .filter(isResearchTheme)
          .sort((a, b) => {
            const aNum = parseInt(a.themeNumber.replace('T', '')) || 999;
            const bNum = parseInt(b.themeNumber.replace('T', '')) || 999;
            return aNum - bNum;
          });
          
      } catch (fallbackError) {
        console.error('Fallback research themes request also failed:', fallbackError);
        return [];
      }
    }
  }

 // =====================
// PEOPLE API
// =====================

async getPeople(personType?: PersonType | PersonType[]): Promise<Person[]> {
  const searchParams = new URLSearchParams();
  
  // Handle single or multiple person types
  if (personType) {
    if (Array.isArray(personType)) {
      // Multiple person types using $in operator - correct Strapi 5 syntax
      personType.forEach((type, index) => {
        searchParams.set(`filters[personType][$in][${index}]`, type);
      });
    } else {
      // Single person type
      searchParams.set('filters[personType][$eq]', personType);
    }
  }
  
  // Ensure we get all records (not just 25)
  searchParams.set('pagination[pageSize]', '100'); // Use 100 instead of -1
  
  // Use simpler populate syntax
  searchParams.set('populate', '*');
  
  // Sort by name
  searchParams.set('sort[0]', 'name:asc');

  console.log('People API URL:', `/people?${searchParams.toString()}`);

  return this.fetchWithErrorHandling<Person[]>(
    `/people?${searchParams.toString()}`,
    []
  );
}

// Specific method for investigators
async getInvestigators(): Promise<Person[]> {
  const investigatorTypes: PersonType[] = [
    'Lead Investigator', 
    'Co-Lead', 
    'Co-Investigator', 
    'Theme Lead',
  ];
  
  return this.getPeople(investigatorTypes);
}

async getAdvisoryBoardMembers(): Promise<Person[]> {
  try {
    const searchParams = new URLSearchParams();
    
    // Filter for advisory board members using $in operator - Strapi 5 syntax
    const advisoryTypes = ['Industry Advisory Board Chair', 'Industry Advisory Board Member'];
    advisoryTypes.forEach((type, index) => {
      searchParams.set(`filters[personType][$in][${index}]`, type);
    });
    
    // Get all records
    searchParams.set('pagination[pageSize]', '100');
    
    // Populate all related data
    searchParams.set('populate', '*');
    
    // Sort by personType (Chair first) then by name
    searchParams.set('sort[0]', 'personType:asc');
    searchParams.set('sort[1]', 'name:asc');

    console.log('Advisory Board API URL:', `/people?${searchParams.toString()}`);

    const response = await this.request<StrapiResponse<Person[]>>(
      `/people?${searchParams.toString()}`
    );

    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid advisory board response structure:', response);
      return [];
    }

    // Sort by custom hierarchy in JavaScript (more reliable than Strapi sorting)
    return response.data.sort((a: Person, b: Person) => {
      // Chair always comes first
      if (a.personType === 'Industry Advisory Board Chair') return -1;
      if (b.personType === 'Industry Advisory Board Chair') return 1;
      
      // Then sort members alphabetically
      return a.name.localeCompare(b.name);
    });

  } catch (error) {
    console.error('Failed to fetch advisory board members:', error);
    
    // Fallback: try a simpler request
    try {
      console.log('Trying fallback advisory board API request...');
      const response = await this.request<StrapiResponse<Person[]>>(
        '/people?populate=*&pagination[pageSize]=100'
      );
      
      if (response.data && Array.isArray(response.data)) {
        // Filter in JavaScript instead
        const advisoryTypes = ['Industry Advisory Board Chair', 'Industry Advisory Board Member'];
        return response.data
          .filter((person: Person) => advisoryTypes.includes(person.personType))
          .sort((a: Person, b: Person) => {
            if (a.personType === 'Industry Advisory Board Chair') return -1;
            if (b.personType === 'Industry Advisory Board Chair') return 1;
            return a.name.localeCompare(b.name);
          });
      }
    } catch (fallbackError) {
      console.error('Fallback advisory board request also failed:', fallbackError);
    }
    
    return [];
  }
}

// Method to get people by multiple criteria
async getPeopleAdvanced(options: {
  personTypes?: PersonType | PersonType[];
  specialRole?: string;
  affiliationId?: number;
  hasPhoto?: boolean;
  limit?: number;
  sortBy?: 'name' | 'personType' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}): Promise<Person[]> {
  const searchParams = new URLSearchParams();
  
  // Person types filter
  if (options.personTypes) {
    const types = Array.isArray(options.personTypes) ? options.personTypes : [options.personTypes];
    types.forEach((type, index) => {
      searchParams.set(`filters[personType][$in][${index}]`, type);
    });
  }
  
  // Special role filter
  if (options.specialRole) {
    searchParams.set('filters[specialRole][$eq]', options.specialRole);
  }
  
  // Affiliation filter
  if (options.affiliationId) {
    searchParams.set('filters[affiliation][id][$eq]', options.affiliationId.toString());
  }
  
  // Has photo filter
  if (options.hasPhoto !== undefined) {
    if (options.hasPhoto) {
      searchParams.set('filters[photo][$notNull]', 'true');
    } else {
      searchParams.set('filters[photo][$null]', 'true');
    }
  }
  
  // Pagination
  const limit = options.limit || 100;
  searchParams.set('pagination[pageSize]', limit.toString());
  
  // Populate related data (Strapi v5 array syntax — `populate[field]=*` is v4
  // and returns 400 in v5).
  searchParams.set('populate[0]', 'photo');
  searchParams.set('populate[1]', 'affiliation');
  searchParams.set('populate[2]', 'researchThemes');
  searchParams.set('populate[3]', 'researchAreas');

  // Sorting
  const sortBy = options.sortBy || 'name';
  const sortOrder = options.sortOrder || 'asc';
  searchParams.set('sort[0]', `${sortBy}:${sortOrder}`);

  return this.fetchWithErrorHandling<Person[]>(
    `/people?${searchParams.toString()}`,
    []
  );
}

// Method to get all people with pagination handling
async getAllPeople(): Promise<Person[]> {
  let allPeople: Person[] = [];
  let page = 1;
  let hasMore = true;

  while (hasMore) {
    try {
      const searchParams = new URLSearchParams();
      searchParams.set('pagination[page]', page.toString());
      searchParams.set('pagination[pageSize]', '100');
      searchParams.set('populate[0]', 'photo');
      searchParams.set('populate[1]', 'affiliation');
      searchParams.set('populate[2]', 'researchThemes');
      searchParams.set('populate[3]', 'researchAreas');
      searchParams.set('sort[0]', 'name:asc');

      const response = await this.request<StrapiResponse<Person[]>>(
        `/people?${searchParams.toString()}`
      );

      if (response.data && Array.isArray(response.data)) {
        allPeople = [...allPeople, ...response.data];
        
        // Check if there are more pages
        const { pagination } = response.meta;
        hasMore = page < (pagination?.pageCount || 1);
        page++;
      } else {
        hasMore = false;
      }
    } catch (error) {
      console.error(`Failed to fetch page ${page}:`, error);
      hasMore = false;
    }
  }

  return allPeople;
}

// Method specifically for the investigators page
async getInvestigatorsWithAllData(): Promise<Person[]> {
  try {
    const searchParams = new URLSearchParams();
    
    // Filter for investigator types using $in operator - Strapi 5 syntax
    const investigatorTypes = ['Lead Investigator', 'Co-Lead', 'Co-Investigator', 'Theme Lead'];
    investigatorTypes.forEach((type, index) => {
      searchParams.set(`filters[personType][$in][${index}]`, type);
    });
    
    // Get all records
    searchParams.set('pagination[pageSize]', '100'); // Use 100 instead of -1
    
    // Populate all related data - use simpler populate syntax
    searchParams.set('populate', '*');
    
    // Sort by name only (personType sorting might cause issues)
    searchParams.set('sort[0]', 'name:asc');

    console.log('Final API URL:', `/people?${searchParams.toString()}`);

    const response = await this.request<StrapiResponse<Person[]>>(
      `/people?${searchParams.toString()}`
    );

    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid investigators response structure:', response);
      return [];
    }

    // Sort by custom role hierarchy in JavaScript (more reliable than Strapi sorting)
    return response.data.sort((a: Person, b: Person) => {
      const roleOrder = {
        'Lead Investigator': 1,
        'Co-Lead': 2,
        'Co-Investigator': 3,
        'Theme Lead': 4,
      };
      
      const aOrder = roleOrder[a.personType as keyof typeof roleOrder] || 99;
      const bOrder = roleOrder[b.personType as keyof typeof roleOrder] || 99;
      
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      
      // Sort by special role if same type
      if (a.specialRole && !b.specialRole) return -1;
      if (!a.specialRole && b.specialRole) return 1;
      
      return a.name.localeCompare(b.name);
    });

  } catch (error) {
    console.error('Failed to fetch investigators:', error);
    
    // Fallback: try a simpler request
    try {
      console.log('Trying fallback API request...');
      const response = await this.request<StrapiResponse<Person[]>>(
        '/people?populate=*&pagination[pageSize]=100'
      );
      
      if (response.data && Array.isArray(response.data)) {
        // Filter in JavaScript instead
        const investigatorTypes = ['Lead Investigator', 'Co-Lead', 'Co-Investigator', 'Theme Lead'];
        return response.data.filter((person: Person) => 
          investigatorTypes.includes(person.personType)
        );
      }
    } catch (fallbackError) {
      console.error('Fallback request also failed:', fallbackError);
    }
    
    return [];
  }
}

// Also add this helper function if you don't have it already:
private transformPersonData(rawPerson: any): Person {
  const person = rawPerson.attributes || rawPerson;
  
  return {
    id: rawPerson.id,
    name: person.name,
    title: person.title,
    email: person.email,
    bio: person.bio,
    personType: person.personType,
    program: person.program,
    projectTitle: person.projectTitle,
    testimonial: person.testimonial,
    linkedProfile: person.linkedProfile,
    specialRole: person.specialRole,
    affiliation: person.affiliation ? {
      name: person.affiliation.name,
      website: person.affiliation.website,
    } : undefined,
    photo: person.photo?.data || person.photo,
    researchThemes: person.researchThemes?.data?.map((theme: any) => ({
      id: theme.id,
      title: theme.attributes?.title || theme.title,
      themeNumber: theme.attributes?.themeNumber || theme.themeNumber,
    })) || [],
    researchAreas: person.researchAreas?.data?.map((area: any) => ({
      id: area.id,
      area: area.attributes?.area || area.area,
      description: area.attributes?.description || area.description,
    })) || [],
    supervisors: person.supervisors?.data?.map((supervisor: any) => this.transformPersonData(supervisor)) || [],
  };
}

// Also add this helper function if you don't have it already:
private transformPersonData(rawPerson: any): Person {
  const person = rawPerson.attributes || rawPerson;
  
  return {
    id: rawPerson.id,
    name: person.name,
    title: person.title,
    email: person.email,
    bio: person.bio,
    personType: person.personType,
    program: person.program,
    projectTitle: person.projectTitle,
    testimonial: person.testimonial,
    linkedProfile: person.linkedProfile,
    specialRole: person.specialRole,
    affiliation: person.affiliation ? {
      name: person.affiliation.name,
      website: person.affiliation.website,
    } : undefined,
    photo: person.photo?.data || person.photo,
    researchThemes: person.researchThemes?.data?.map((theme: any) => ({
      id: theme.id,
      title: theme.attributes?.title || theme.title,
      themeNumber: theme.attributes?.themeNumber || theme.themeNumber,
    })) || [],
    researchAreas: person.researchAreas?.data?.map((area: any) => ({
      id: area.id,
      area: area.attributes?.area || area.area,
      description: area.attributes?.description || area.description,
    })) || [],
    supervisors: person.supervisors?.data?.map((supervisor: any) => this.transformPersonData(supervisor)) || [],
  };
}

  // =====================
  // SITE SETTINGS API
  // =====================

  async getSiteSettings(): Promise<Nullable<SiteSettings>> {
    try {
      const response = await this.request<StrapiSingleResponse<SiteSettings>>(
        '/site-settings?populate=*'
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch site settings:', error);
      return null;
    }
  }

  // =====================
  // CONTACT INFORMATION API
  // =====================

// =====================
// RESOURCES API
// =====================

async getResources(params?: {
  resourceType?: ResourceType;
  category?: string;
  accessLevel?: string;
  page?: number;
  pageSize?: number;
}): Promise<Resource[]> {
  try {
    console.log('Fetching resources with params:', params);
    
    const searchParams = new URLSearchParams();
    
    // Pagination
    if (params?.page) {
      searchParams.set('pagination[page]', params.page.toString());
    }
    if (params?.pageSize) {
      searchParams.set('pagination[pageSize]', params.pageSize.toString());
    }
    
    // Filters
    if (params?.resourceType) {
      searchParams.set('filters[resourceType][$eq]', params.resourceType);
    }
    
    if (params?.category) {
      searchParams.set('filters[category][$eq]', params.category);
    }
    
    if (params?.accessLevel) {
      searchParams.set('filters[accessLevel][$eq]', params.accessLevel);
    }

    // Populate related data
    searchParams.set('populate', '*');
    searchParams.set('sort', 'title:asc');

    const url = `/resources?${searchParams.toString()}`;
    console.log('Resources API URL:', url);

    const response = await this.request<StrapiResponse<Resource[]>>(url);
    
    console.log('Resources response:', response);
    
    if (!response.data || !Array.isArray(response.data)) {
      console.error('Invalid resources response structure:', response);
      return [];
    }
    
    return response.data;
    
  } catch (error) {
    console.error('Failed to fetch resources:', error);
    
    // Try fallback request
    try {
      console.log('Trying fallback resources request...');
      const fallbackResponse = await this.request<StrapiResponse<Resource[]>>(
        '/resources?populate=*'
      );
      
      return fallbackResponse.data || [];
    } catch (fallbackError) {
      console.error('Fallback resources request also failed:', fallbackError);
      return [];
    }
  }
}

async getPublicResources(limit: number = 20): Promise<Resource[]> {
  try {
    const response = await this.request<StrapiResponse<Resource[]>>(
      `/resources?filters[accessLevel][$eq]=Public&populate=*&pagination[pageSize]=${limit}&sort=title:asc`
    );
    
    return response.data || [];
    
  } catch (error) {
    console.error('Failed to fetch public resources:', error);
    return [];
  }
}

async getResourcesByType(resourceType: ResourceType, limit?: number): Promise<Resource[]> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set('filters[resourceType][$eq]', resourceType);
    searchParams.set('populate', '*');
    searchParams.set('sort', 'title:asc');
    if (limit) {
      searchParams.set('pagination[pageSize]', limit.toString());
    }
    
    const response = await this.request<StrapiResponse<Resource[]>>(
      `/resources?${searchParams.toString()}`
    );
    
    return response.data || [];
    
  } catch (error) {
    console.error(`Failed to fetch ${resourceType} resources:`, error);
    return [];
  }
}

async getResourcesByCategory(category: string, limit?: number): Promise<Resource[]> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.set('filters[category][$eq]', category);
    searchParams.set('populate', '*');
    searchParams.set('sort', 'title:asc');
    if (limit) {
      searchParams.set('pagination[pageSize]', limit.toString());
    }

    const response = await this.request<StrapiResponse<Resource[]>>(
      `/resources?${searchParams.toString()}`
    );

    return response.data || [];

  } catch (error) {
    console.error(`Failed to fetch ${category} resources:`, error);
    return [];
  }
}

  // =====================
  // PUBLICATIONS API
  // =====================

  // Sort: newest publication year first, then most recently uploaded first.
  async getPublications(): Promise<Publication[]> {
    try {
      const searchParams = new URLSearchParams();
      searchParams.set('populate', '*');
      searchParams.set('pagination[pageSize]', '100');
      searchParams.set('sort[0]', 'publicationYear:desc');
      searchParams.set('sort[1]', 'createdAt:desc');

      const response = await this.request<StrapiResponse<Publication[]>>(
        `/publications?${searchParams.toString()}`
      );
      return response.data || [];
    } catch (error) {
      console.error('Failed to fetch publications:', error);
      return [];
    }
  }

  // =====================
  // EVENTS API
  // =====================

  async getEvents(params?: {
    upcoming?: boolean;
    eventType?: EventType;
    page?: number;
    pageSize?: number;
  }): Promise<Event[]> {
    try {
      console.log('Fetching events with params:', params);
      
      const searchParams = new URLSearchParams();
      
      // Pagination
      if (params?.page) {
        searchParams.set('pagination[page]', params.page.toString());
      }
      if (params?.pageSize) {
        searchParams.set('pagination[pageSize]', params.pageSize.toString());
      }
      
      // Filters
      if (params?.eventType) {
        searchParams.set('filters[eventType][$eq]', params.eventType);
      }
      
      if (params?.upcoming !== undefined) {
        const now = new Date().toISOString();
        if (params.upcoming) {
          searchParams.set('filters[startDateTime][$gte]', now);
        } else {
          searchParams.set('filters[startDateTime][$lt]', now);
        }
      }

      // Populate and sort
      searchParams.set('populate', '*');
      searchParams.set('sort', 'startDateTime:asc');

      const url = `/events?${searchParams.toString()}`;
      console.log('Events API URL:', url);

      const response = await this.request<StrapiResponse<Event[]>>(url);
      
      console.log('Events response:', response);
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Invalid events response structure:', response);
        return [];
      }
      
      return response.data;
      
    } catch (error) {
      console.error('Failed to fetch events:', error);
      
      // Try fallback request
      try {
        console.log('Trying fallback events request...');
        const fallbackResponse = await this.request<StrapiResponse<Event[]>>(
          '/events?populate=*'
        );
        
        return fallbackResponse.data || [];
      } catch (fallbackError) {
        console.error('Fallback events request also failed:', fallbackError);
        return [];
      }
    }
  }

  async getUpcomingEvents(limit: number = 10): Promise<Event[]> {
    return this.getEvents({ upcoming: true, pageSize: limit });
  }

  async getPastEvents(limit: number = 10): Promise<Event[]> {
    return this.getEvents({ upcoming: false, pageSize: limit });
  }
}

// =====================
// UTILITY FUNCTIONS
// =====================

// Always route media through the configured Strapi host. Strips any host on
// absolute URLs (e.g. when a DB restored from prod has full URLs baked in)
// so the browser only ever fetches uploads from STRAPI_URL.
export const normalizeMediaUrl = (url?: string | null): string => {
  if (!url) return '';
  if (url.startsWith('/')) return `${STRAPI_URL}${url}`;
  try {
    const parsed = new URL(url);
    return `${STRAPI_URL}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
};

export const strapiHelpers = {
  // Get full URL for Strapi media
  getMediaUrl: (media?: StrapiMedia): string => {
    if (!media || !isStrapiMedia(media)) return '';
    return normalizeMediaUrl(media.url);
  },

  // Get responsive image URLs
  getResponsiveImageUrls: (media?: StrapiMedia) => {
    if (!media || !isStrapiMedia(media)) return {};

    const { formats } = media;
    const baseUrl = normalizeMediaUrl(media.url);

    return {
      original: baseUrl,
      large: formats?.large ? normalizeMediaUrl(formats.large.url) : baseUrl,
      medium: formats?.medium ? normalizeMediaUrl(formats.medium.url) : baseUrl,
      small: formats?.small ? normalizeMediaUrl(formats.small.url) : baseUrl,
      thumbnail: formats?.thumbnail ? normalizeMediaUrl(formats.thumbnail.url) : baseUrl,
    };
  },

  // Format date for display
  formatDate: (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
    try {
      return new Date(dateString).toLocaleDateString('en-CA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options,
      });
    } catch (error) {
      console.error('Invalid date string:', dateString);
      return dateString;
    }
  },

  // Extract plain text from rich text content
  extractPlainText: (richText?: string, maxLength?: number): string => {
    if (!richText) return '';
    
    // Remove markdown/HTML-like formatting
    let plainText = richText
      .replace(/[#*_`]/g, '') // Remove markdown formatting
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim();

    if (maxLength && plainText.length > maxLength) {
      plainText = plainText.substring(0, maxLength).trim() + '...';
    }

    return plainText;
  },

  // Sort people by special roles (for leadership display)
  sortPeopleByRole: (people: Person[]): Person[] => {
    const roleOrder = {
      'Program Director': 1,
      'Co-Director': 2,
      'Lead Investigator': 3,
      'Co-Lead Investigator': 4,
      'Chair': 5,
      'Vice-Chair': 6,
    } as const;

    return people.sort((a, b) => {
      const aRole = a.attributes.specialRole;
      const bRole = b.attributes.specialRole;
      
      if (aRole && bRole) {
        return (roleOrder[aRole] || 999) - (roleOrder[bRole] || 999);
      }
      if (aRole && !bRole) return -1;
      if (!aRole && bRole) return 1;
      
      // Fallback to alphabetical by name
      return a.attributes.name.localeCompare(b.attributes.name);
    });
  },
};

// =====================
// EXPORTS
// =====================

// Create and export API client instance
export const api = new ApiClient();

// Export commonly used functions
export const {
  getMediaUrl,
  getResponsiveImageUrls,
  formatDate,
  extractPlainText,
  sortPeopleByRole,
} = strapiHelpers;

// Re-export types for convenience
export type {
  Homepage,
  NewsArticle,
  Organization,
  ResearchTheme,
  SiteSettings,
  ContactInformation,
  Person,
  StrapiMedia,
  NewsCategory,
  OrganizationType,
  PartnershipLevel,
  PersonType,
  ApiResult,
  Nullable,
  PaginationParams,
  SearchFilters,
} from '@/types';